/* eslint-disable import/no-cycle */
import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { WrappedFunctionProps } from '../types';
import TabFilter, { TabFilterOption } from '../../utilComponents/TabFilter';
import { flattenUISchema, hasMappedChildren } from '../helper';
import useDynaFormFactory from '../useDynaFormFactory';
import SubmitContext from '../FormContext';

/** Usage
* someTabset: {
*   component: 'Tabset',
*   translation: {
*     title: {
*       de: 'Ein Tabset',
*       en: 'A tabset',
*     },
*     tabNames: {
*       someTab: {
*         de: 'Ein Tab',
*         en: 'Some tab',
*       },
*       someOtherTab: {
*         de: 'Ein anderer Tab',
*         en: 'Some other tab',
*       },
*     },
*   },
*   tabs: {
*     someTab: {
*       someInput: {
*         component: 'TextField',
*         ...
*       },
*     },
*     someOtherTab: {
*       someOtherInput: {
*         component: 'TextField',
*         ...
*       },
*     },
*   },
},
*/

export default function Tabset({
  translation,
  control,
  language,
  data,
}: WrappedFunctionProps) {
  const { toElements } = useDynaFormFactory();
  const [tabRoot, setTabRoot] = useState<ReactNode[]>([]);

  const forceSubmit = useContext(SubmitContext);

  const options: TabFilterOption[] = data?.tabs
    ? Object.keys(data.tabs)
        .map((tabId) => ({
          id: tabId,
          title: translation.tabNames?.[tabId]?.[language] || tabId,
        }))
        .filter((tab) =>
          hasMappedChildren(data.tabs[tab.id], data.currentScope),
        )
    : [];

  const [currentTab, setCurrentTab] = useState<TabFilterOption>(options[0]);
  const changeTab = useCallback(
    async (tab: TabFilterOption) => {
      if (!forceSubmit || !(await forceSubmit())) return;
      setCurrentTab(tab);
    },
    [forceSubmit],
  );

  useEffect(() => {
    if (!data?.tabs[currentTab?.id]) return;
    setTabRoot(
      toElements(
        {
          elementDescriptions: flattenUISchema(data.tabs[currentTab?.id]),
          mapping: data.mapping,
        },
        {
          control,
          language,
          currentScope: data.currentScope,
          namePrefix: data.namePrefix,
        },
      ),
    );
  }, [
    control,
    currentTab?.id,
    data.currentScope,
    data.mapping,
    data.namePrefix,
    data.tabs,
    data.watch,
    language,
    toElements,
  ]);

  if (!data?.tabs || !Object.keys(data.tabs).length) return null;

  return (
    <>
      <TabFilter
        options={options}
        defaultOption={currentTab}
        onChange={changeTab}
        value={currentTab}
        sx={{ marginBottom: '32px' }}
      />
      {tabRoot}
    </>
  );
}
